import React, { useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { FormattedMessage } from "react-intl";
import { format } from "date-fns";

import { Heading5, Row, Col, Icon } from "../";
import Loading from "../Loading";

/* ─── Slot popover ──────────────────────────────────────────────────────── */

const PopoverBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PopoverContent = styled.div`
  background: ${props => props.theme.white};
  border-radius: ${props => props.theme.borderRadius};
  padding: 1.5rem;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: ${props => props.theme.boxShadowLifted};
  position: relative;
`;

const PopoverClose = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  border: none;
  background: none;
  cursor: pointer;
  color: ${props => props.theme.gray600};
  font-size: 1.25rem;
  line-height: 1;
  padding: 0.25rem;
  &:hover {
    color: ${props => props.theme.gray900};
  }
`;

const SlotItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.6rem 1rem;
  margin-bottom: 0.5rem;
  border-radius: ${props => props.theme.borderRadius};
  border: 1px solid ${props => props.theme.gray200};
  background-color: ${props => props.theme.white};
  color: ${props => props.theme.gray900};
  cursor: pointer;
  font-size: ${props => props.theme.fontSizes.default};
  transition: background-color 0.2s, border-color 0.2s;

  &:hover:not(:disabled) {
    border-color: ${props => props.theme.info};
    background-color: ${props => props.theme.gray100};
  }
`;

const SlotTime = styled.span`
  font-weight: 600;
`;

const SlotAction = styled.span`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.gray600};
`;

const UserGroupWrapper = styled(Col)`
  margin-bottom: 1rem;
`;

const UserGroupLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.small};
  font-weight: 600;
  color: ${props => props.theme.gray700};
  margin: 0.75rem 0 0.25rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid ${props => props.theme.gray200};
`;

const LoadingWrapper = styled.div`
  padding: 2rem;
  text-align: center;
`;

const NoSlotsMessage = styled.div`
  color: ${props => props.theme.gray600};
  font-size: ${props => props.theme.fontSizes.small};
  text-align: center;
  padding: 1rem 0;
`;

const TimeSlotPicker = ({
  isOpen,
  selectedDate,
  availabilities,
  loadingSlots,
  slotsError,
  onClose,
  onSlotSelect,
}) => {
  // Close popover on Escape key
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <PopoverBackdrop onClick={onClose}>
      <PopoverContent onClick={e => e.stopPropagation()}>
        <PopoverClose
          type="button"
          onClick={onClose}
          aria-label="Close"
        >
          <Icon name="add" direction={45} />
        </PopoverClose>

        <Heading5>
          <FormattedMessage
            id="components.CalendarPicker.availableSlots"
            values={{ date: selectedDate ? format(selectedDate, "dd.MM.yyyy") : "" }}
          />
        </Heading5>

        {loadingSlots && (
          <LoadingWrapper>
            <Loading text={<FormattedMessage id="loading" />} small />
          </LoadingWrapper>
        )}
        {!loadingSlots && slotsError && (
          <NoSlotsMessage>
            <FormattedMessage id="components.CalendarPicker.slotsError" />
          </NoSlotsMessage>
        )}
        {!loadingSlots && !slotsError && availabilities.length === 0 && (
          <NoSlotsMessage>
            <FormattedMessage id="components.CalendarPicker.noSlots" />
          </NoSlotsMessage>
        )}
        {!loadingSlots && (() => {
          // Group availabilities by userId; fall back to ungrouped if no userId present
          const hasUserIds = availabilities.some(s => s.userId != null);
          if (!hasUserIds) {
            return availabilities.map(slot => (
              <SlotItem
                key={`${slot.fromTime}-${slot.toTime}`}
                type="button"
                onClick={() => onSlotSelect(slot)}
              >
                <SlotTime>
                  {slot.fromTime} – {slot.toTime}
                </SlotTime>
                <SlotAction>
                  <FormattedMessage id="components.CalendarPicker.selectSlot" />
                </SlotAction>
              </SlotItem>
            ));
          }

          // Build ordered list of unique users preserving first-seen order
          const userOrder = [];
          const groups = {};
          availabilities.forEach(slot => {
            const uid = slot.userId != null ? String(slot.userId) : "__unknown__";
            if (!groups[uid]) {
              groups[uid] = { userName: slot.userName || uid, slots: [] };
              userOrder.push(uid);
            }
            groups[uid].slots.push(slot);
          });

          return (
            <Row>
              {userOrder.map(uid => (
                <UserGroupWrapper count={12 / userOrder.length} key={uid}>
                  <UserGroupLabel>{groups[uid].userName}</UserGroupLabel>
                  {groups[uid].slots.map(slot => (
                    <SlotItem
                      key={`${uid}-${slot.fromTime}-${slot.toTime}`}
                      type="button"
                      onClick={() => onSlotSelect(slot)}
                    >
                      <SlotTime>
                        {slot.fromTime} – {slot.toTime}
                      </SlotTime>
                      <SlotAction>
                        <FormattedMessage id="components.CalendarPicker.selectSlot" />
                      </SlotAction>
                    </SlotItem>
                  ))}
                </UserGroupWrapper>
              ))}
            </Row>
          );
        })()}
      </PopoverContent>
    </PopoverBackdrop>
  );
};

TimeSlotPicker.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  selectedDate: PropTypes.object,
  availabilities: PropTypes.array.isRequired,
  loadingSlots: PropTypes.bool.isRequired,
  slotsError: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSlotSelect: PropTypes.func.isRequired,
};

export default TimeSlotPicker;